import { Fragment, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DialogClose, DialogTitle } from '@/components/ui/dialog';
import { Icon } from '@/components/ui/icon';
import { useToast } from '@/hooks/use-toast';
import { daysUntilDate, formatDateOnly } from '@/lib/date';
import { applyBestMask } from '@/lib/mask';
import { formatSizeBreakdown } from '@/lib/sale-item-sizes';
import { isSaleEditable } from '@/lib/sale-status';
import { cn } from '@/lib/utils';
import { useDynamicList } from '@/services/dynamic-resource';
import { itemDescription, money } from './sale-detail';
import { DeliveryBadge, StatusBadge } from './status';

interface SaleQuickViewProps {
  sale: any;
  items: any[];
  onEdit?: () => void;
  /** Renderiza fora de um `<Dialog>` (página de visualização) — sem título/close do Radix. */
  standalone?: boolean;
}

async function downloadFile(endpoint: string, filename: string) {
  const res = await fetch(endpoint);
  if (!res.ok) throw new Error('Falha ao gerar PDF');

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** "Produto: X\nQuantidade: Y" (formato usado pelo bot do WhatsApp) → pares rótulo/valor. */
function parseBudgetNotes(notes: string): { label: string; value: string }[] {
  return notes
    .split('\n')
    .map((line) => {
      const match = line.match(/^([^:]+):\s*(.*)$/);
      if (!match) return null;
      return { label: match[1]!.trim(), value: match[2]!.trim() };
    })
    .filter((entry): entry is { label: string; value: string } => !!entry);
}

function isOverdue(sale: any) {
  if (['delivered', 'canceled'].includes(sale.status)) return false;
  return daysUntilDate(sale.deliveryDate) < 0;
}

/** Corpo do modal de visualização rápida do pedido (kanban/tabela) — layout dedicado, distinto do PDF/impressão. */
export function SaleQuickView({ sale, items, onEdit, standalone }: SaleQuickViewProps) {
  const toast = useToast();
  const [downloading, setDownloading] = useState(false);
  const [downloadingCuttingMap, setDownloadingCuttingMap] = useState(false);
  const [sendingPdf, setSendingPdf] = useState(false);
  const { data: optionsResponse } = useDynamicList('additional-options', {
    perPage: 200,
  });
  const options: any[] = optionsResponse?.data ?? [];
  const optionLabel = (id: string) => options.find((o) => o.id === id)?.label ?? id;

  const depositAmount = sale.depositAmount ?? 0;
  const balanceAmount = (sale.total ?? 0) - depositAmount;
  const subtotal = items.reduce((sum, item) => sum + (item.totalPrice ?? 0), 0);
  const totalPieces = items.reduce((sum, item) => sum + (item.quantity ?? 0), 0);
  const budgetEntries = sale.budgetNotes ? parseBudgetNotes(sale.budgetNotes) : [];

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadFile(
        `/api/v1/sales/${sale.id}/pdf`,
        `pedido-${sale.saleNumber ?? sale.id.slice(0, 8)}.pdf`,
      );
    } catch {
      toast.error('Não foi possível gerar o PDF. Tente novamente.');
    } finally {
      setDownloading(false);
    }
  };

  const handleSendPdf = async () => {
    setSendingPdf(true);
    try {
      const res = await fetch(`/api/v1/sales/${sale.id}/send-pdf`, { method: 'POST' });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message ?? 'Falha ao enviar PDF');
      toast.success('PDF enviado ao cliente pelo WhatsApp.');
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Não foi possível enviar o PDF ao cliente. Tente novamente.',
      );
    } finally {
      setSendingPdf(false);
    }
  };

  const handleDownloadCuttingMap = async () => {
    setDownloadingCuttingMap(true);
    try {
      await downloadFile(
        `/api/v1/sales/${sale.id}/cutting-map`,
        `mapa-de-corte-${sale.saleNumber ?? sale.id.slice(0, 8)}.pdf`,
      );
    } catch {
      toast.error('Não foi possível gerar o Mapa de Corte. Tente novamente.');
    } finally {
      setDownloadingCuttingMap(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-start gap-3 p-5">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-[10px] bg-primary-soft text-primary">
          <Icon name="shirt" className="size-[18px]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {standalone ? (
              <h1 className="font-display text-[19px] font-extrabold tracking-[-0.01em]">
                Pedido {sale.saleNumber ?? `#${sale.id.slice(0, 8)}`}
              </h1>
            ) : (
              <DialogTitle className="font-display text-[19px] font-extrabold tracking-[-0.01em]">
                Pedido {sale.saleNumber ?? `#${sale.id.slice(0, 8)}`}
              </DialogTitle>
            )}
            <StatusBadge status={sale.status} />
            {!['delivered', 'canceled'].includes(sale.status) && (
              <DeliveryBadge deliveryDate={sale.deliveryDate} status={sale.status} />
            )}
          </div>
          <p className="mt-0.5 truncate text-[13px] text-muted-foreground">
            {sale.customer?.name ?? '—'} · criado em{' '}
            {new Date(sale.createdAt).toLocaleDateString('pt-BR')} · {totalPieces} peças
          </p>
        </div>
        {!standalone && (
          <DialogClose className="rounded-[8px] p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
            <Icon name="x" className="size-4" />
            <span className="sr-only">Fechar</span>
          </DialogClose>
        )}
      </div>

      {/* Ações */}
      <div className="flex flex-wrap items-center gap-2 border-y border-border px-5 py-3">
        {onEdit && isSaleEditable(sale.status) && (
          <Button icon="pencil" size="sm" onClick={onEdit}>
            Editar pedido
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          icon="download"
          disabled={downloading}
          onClick={handleDownload}
        >
          {downloading ? 'Gerando...' : 'Baixar PDF'}
        </Button>
        {sale.customer?.phone && (
          <Button
            variant="outline"
            size="sm"
            icon="send"
            disabled={sendingPdf}
            onClick={handleSendPdf}
          >
            {sendingPdf ? 'Enviando...' : 'Enviar ao cliente'}
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          icon="scissors"
          disabled={downloadingCuttingMap}
          onClick={handleDownloadCuttingMap}
        >
          {downloadingCuttingMap ? 'Gerando...' : 'Mapa de corte'}
        </Button>
      </div>

      <div className="space-y-4 p-5">
        {/* Meta grid */}
        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-[10px] border border-border bg-border md:grid-cols-5">
          <MetaTile label="Cliente" value={sale.customer?.name ?? '—'} />
          <MetaTile
            label="CPF / CNPJ"
            value={
              sale.customer?.documentNumber
                ? applyBestMask(sale.customer.documentNumber, [
                    '999.999.999-99',
                    '99.999.999/9999-99',
                  ])
                : '—'
            }
            mono
          />
          <MetaTile
            label="Telefone"
            value={
              sale.customer?.phone
                ? applyBestMask(sale.customer.phone, [
                    '(99) 9999-9999',
                    '(99) 99999-9999',
                  ])
                : '—'
            }
            mono
          />
          <MetaTile label="Pagamento" value={sale.paymentMethod?.description ?? '—'} />
          <MetaTile
            label="Entrega"
            value={formatDateOnly(sale.deliveryDate)}
            mono
            destructive={isOverdue(sale)}
          />
        </div>

        {/* Composição */}
        <div className="overflow-hidden rounded-[10px] border border-border">
          <div className="grid grid-cols-[1fr_64px_96px_96px] bg-raised px-3 py-2 text-[9.5px] font-bold uppercase tracking-[0.08em] text-faint">
            <span>Composição</span>
            <span className="text-center">Qtd.</span>
            <span className="text-right">Unitário</span>
            <span className="text-right">Total</span>
          </div>
          {items.map((item) => (
            <div
              key={item.id}
              className="grid grid-cols-[1fr_64px_96px_96px] items-start border-t border-border px-3 py-2.5 text-sm"
            >
              <div className="min-w-0 pr-2">
                <div className="font-medium">{itemDescription(item)}</div>
                <div className="mt-0.5 font-mono text-xs text-muted-foreground">
                  {formatSizeBreakdown(item.sizeBreakdown) ?? (
                    <span className="font-sans text-warning">Tamanho pendente</span>
                  )}
                </div>
                {((item.selectedOptions?.length ?? 0) > 0 || item.artworkPath) && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {(item.selectedOptions ?? []).map((id: string) => (
                      <Badge key={id} variant="outline">
                        {optionLabel(id)}
                      </Badge>
                    ))}
                    {item.artworkPath && <Badge variant="outline">Arte anexada</Badge>}
                  </div>
                )}
              </div>
              <div className="text-center">{item.quantity}</div>
              <div className="text-right font-mono">{money(item.unitaryPrice)}</div>
              <div className="text-right font-mono font-medium">
                {money(item.totalPrice)}
              </div>
            </div>
          ))}
        </div>

        {/* Rodapé */}
        <div
          className={cn(
            'grid grid-cols-1 gap-4',
            sale.budgetNotes && 'lg:grid-cols-[1fr_320px]',
          )}
        >
          {sale.budgetNotes && (
            <div className="rounded-[10px] border border-dashed border-border p-3.5">
              <div className="flex items-center gap-1.5 text-[9.5px] font-bold uppercase tracking-[0.08em] text-faint">
                <Icon name="message-circle" className="size-3" />
                Coletado no orçamento (WhatsApp)
              </div>
              <div className="mt-2.5 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-[13px]">
                {budgetEntries.map((entry) => (
                  <Fragment key={entry.label}>
                    <span className="text-muted-foreground">{entry.label}</span>
                    <span className="font-medium">{entry.value}</span>
                  </Fragment>
                ))}
              </div>
              <div className="mt-3 border-t border-dashed border-border pt-2.5 text-[13px] text-muted-foreground">
                Nota fiscal:{' '}
                <strong className="text-foreground">
                  {sale.invoiceNumber ?? 'não informada'}
                </strong>
              </div>
            </div>
          )}

          <div className="space-y-1.5 rounded-[10px] border border-border bg-raised p-4">
            <div className="flex justify-between text-[13px] text-muted-foreground">
              <span>Subtotal peças</span>
              <span className="font-mono">{money(subtotal)}</span>
            </div>
            {(sale.operationalCost ?? 0) > 0 && (
              <div className="flex justify-between text-[13px] text-muted-foreground">
                <span>Custo operacional</span>
                <span className="font-mono">{money(sale.operationalCost)}</span>
              </div>
            )}
            <div className="flex items-center justify-between text-[13px] text-muted-foreground">
              <span>Entrada</span>
              <div className="flex items-center gap-2">
                <Badge variant={sale.depositPaidAt ? 'success' : 'warning'}>
                  {sale.depositPaidAt ? 'Pago' : 'Pendente'}
                </Badge>
                <span className="font-mono text-foreground">{money(depositAmount)}</span>
              </div>
            </div>
            <div className="flex items-center justify-between text-[13px] text-muted-foreground">
              <span>Saldo</span>
              <div className="flex items-center gap-2">
                <Badge variant={sale.balancePaidAt ? 'success' : 'warning'}>
                  {sale.balancePaidAt ? 'Pago' : 'Pendente'}
                </Badge>
                <span className="font-mono text-foreground">{money(balanceAmount)}</span>
              </div>
            </div>
            <div className="flex items-center justify-between border-t border-border-strong pt-2.5">
              <span className="text-sm font-semibold">Total</span>
              <span className="font-display text-2xl font-extrabold tracking-[-0.02em]">
                {money(sale.total)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetaTile({
  label,
  value,
  mono,
  destructive,
}: {
  label: string;
  value: string;
  mono?: boolean;
  destructive?: boolean;
}) {
  return (
    <div className="bg-card p-3">
      <div className="text-[9.5px] font-bold uppercase tracking-[0.08em] text-faint">
        {label}
      </div>
      <div
        className={cn(
          'mt-0.5 truncate text-[13px] font-medium',
          mono && 'font-mono',
          destructive && 'text-destructive',
        )}
      >
        {value}
      </div>
    </div>
  );
}
